import { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { SessionUser } from "@/api/auth";
import {
  useRoleChangeLogs,
  useSession,
  useSetUserRole,
  useSwitchView,
  useUsers,
} from "@/api/queries";
import type { RoleChangeLog, SetRoleInput } from "@/api/types";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { Panel, SectionTitle } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { SegmentControl } from "@/components/SegmentControl";
import { Select } from "@/components/Select";
import { StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import { CREATION_ROLE_LABEL, viewModeLabel } from "@/lib/session";
import { useUiStore } from "@/store/ui";

/** 可授予的创作子角色（**不含 `admin`**：那是首个账号的引导值，`ADR-025` D3）。 */
type CreationRole = NonNullable<SetRoleInput["creation_role"]>;

const CREATION_ROLES: { value: CreationRole; label: string }[] = [
  { value: "head_writer", label: "总编剧" },
  { value: "writer", label: "编剧" },
  { value: "designer", label: "设计" },
  { value: "reviewer", label: "审阅" },
];

const ACTION_LABELS: Record<RoleChangeLog["action"], string> = {
  switch_view: "切换身份",
  set_role: "设置角色",
};

/**
 * 账号管理（`specs/15` §2 追加节 · `ADR-025`）。
 *
 * 三块内容，对应产品方 2026-10-06 的三项要求：
 * ① **角色设置**（admin）——可升可降，含"降回观众"这个此前没有的方向；
 * ② **身份切换**（本人）——观众 ⇄ 编剧随时切，**只在授权范围内**；
 * ③ **操作记录**——每次真实变更一行，落库可查。
 *
 * 为什么放在 `/account` 而**不**在 `/studio/**`：观众必须能进来（他要看自己的记录、
 * 知道自己为什么切不了）。`RequireEditorView` 是当前身份为编剧的门，进不去就等于这个视图
 * 对玩家不存在（`ADR-025` D6）。
 *
 * 权限**全部由后端判**（`set-role` 仅 admin、`switch-view` 越权即 403）。
 * 前端的禁用与隐藏只是"不提供无效入口"，不是边界（CONSTITUTION A3）。
 *
 * @dataSource remote
 */
export function AccountView() {
  const navigate = useNavigate();
  const session = useSession();
  const user = session.data ?? null;
  const switchView = useSwitchView();
  const logs = useRoleChangeLogs(undefined, Boolean(user));
  // ⚠️ **所有 Hook 必须在任何提前 return 之前**（React 的规则）。
  // 放在下面的 `if (!user) return …` 之后 ⇒ 未登录时少调一个 Hook，渲染顺序随身份变化。
  const pushToast = useUiStore((state) => state.pushToast);

  if (session.isPending) return <div className="page-scroll" aria-busy="true" />;
  // 未登录 → 回登录页（与 `RequireEditorView` 同一出口）。
  if (session.isError || !user) {
    return (
      <div className="account-view">
        <EmptyState
          title="尚未登录"
          description="账号管理需要登录后才能查看与设置。"
          action={
            <Button variant="primary" onClick={() => navigate("/login", { replace: true })}>
              去登录
            </Button>
          }
        />
      </div>
    );
  }

  const canSwitchToCreator = user.platform_role === "editor";

  return (
    <div className="account-view page-scroll">
      <SectionTitle
        title="账号与身份"
        description="角色决定你能做什么，身份决定你此刻看到哪一侧界面——切换身份不会改变你的权限。"
      />

      <Panel
        title="我的身份"
        description="未获授权的观众无法切到编剧视角；已获授权后可随时来回切换，误点切回去即可，不会丢失权限。"
        actions={
          canSwitchToCreator ? (
            <Tag tone="green">可随时切换</Tag>
          ) : (
            <Tag tone="gold">观众账号 · 需管理员开通编辑身份</Tag>
          )
        }
        className="account-identity"
      >
        <div className="account-identity-row">
          <div className="account-identity-facts">
            <div>
              <span className="account-fact-label">账号</span>
              <strong>{user.display_name || user.email}</strong>
              <small>{user.email}</small>
            </div>
            <div>
              <span className="account-fact-label">授权角色</span>
              <StatusLabel tone={canSwitchToCreator ? "success" : "warning"}>
                {canSwitchToCreator
                  ? user.creation_role
                    ? (CREATION_ROLE_LABEL[user.creation_role] ?? user.creation_role)
                    : "编辑"
                  : "观众"}
              </StatusLabel>
            </div>
            <div>
              <span className="account-fact-label">当前身份</span>
              <StatusLabel tone={user.view_mode === "editor" ? "success" : "default"}>
                {viewModeLabel(user.view_mode)}
              </StatusLabel>
            </div>
          </div>

          <div className="account-identity-switch">
            <SegmentControl
              label="切换当前身份"
              value={user.view_mode}
              options={[
                { value: "player", label: "以观众身份" },
                { value: "editor", label: "以编剧身份" },
              ]}
              onChange={(next) => {
                if (next === user.view_mode) return;
                switchView.mutate(next, {
                  onSuccess: (updated) =>
                    pushToast({
                      title: "已切换身份",
                      detail: `现以${viewModeLabel(updated.view_mode)}浏览${
                        updated.view_mode === "player" ? "，创作权限仍然保留" : ""
                      }`,
                      mark: "✓",
                    }),
                  // ⚠️ `useSwitchView` 声明了 `errorHandled: true`（跳过公共兜底），
                  // 所以**每个调用点都必须自呈现失败**，否则 403 会静默消失。
                  // 最可能的一条：未获授权却点了「以编剧身份」⇒ `ROLE_NOT_GRANTED`。
                  onError: (error) =>
                    pushToast({
                      title: "切换失败",
                      detail: error instanceof Error ? error.message : "未知错误",
                      mark: "!",
                    }),
                });
              }}
            />
            {!canSwitchToCreator ? (
              <p className="inline-note">
                你还没有编辑身份。请联系管理员在下方「账号与角色」中开通。
              </p>
            ) : null}
          </div>
        </div>
      </Panel>

      <MyRoleChanges logs={logs} />

      {user.platform_role === "editor" && user.creation_role === "admin" ? (
        <AdminRoleSection />
      ) : null}
    </div>
  );
}

/* ── 我的操作记录 ─────────────────────────────────────────────────────────── */

function MyRoleChanges({ logs }: { logs: ReturnType<typeof useRoleChangeLogs> }) {
  return (
    <Panel
      title="我的角色操作记录"
      description="每一次真实变更都会落库；切到当前身份这种「什么都没发生」的操作不记。"
      className="account-changes"
    >
      <RemoteState queries={[logs]} title="记录加载失败" loadingText="加载中…">
        {(logs.data ?? []).length === 0 ? (
          <EmptyState title="暂无记录" description="角色变更发生后会出现在这里。" />
        ) : (
          <RoleChangeTable rows={logs.data ?? []} />
        )}
      </RemoteState>
    </Panel>
  );
}

/* ── admin：账号与角色设置 ────────────────────────────────────────────────── */

function AdminRoleSection() {
  const users = useUsers(true);
  return (
    <section className="account-admin">
      <SectionTitle
        title="账号与角色"
        description="可为任意账号开通编辑身份，也可降回观众。降级会同时清空其创作角色并把身份收敛为观众。"
        actions={<Tag>仅管理员可见</Tag>}
      />
      <RemoteState queries={[users]} title="账号列表加载失败" loadingText="加载中…">
        <div className="panel" style={{ padding: 0 }}>
          {(users.data ?? []).map((account) => (
            <UserRoleRow key={account.id} account={account} />
          ))}
        </div>
      </RemoteState>
      <AllRoleChanges />
    </section>
  );
}

function UserRoleRow({ account }: { account: SessionUser }) {
  const setRole = useSetUserRole();
  const pushToast = useUiStore((state) => state.pushToast);
  const isEditor = account.platform_role === "editor";
  // 当前展示的创作子角色：editor 取库里的值；player 取本行**本地**选择。
  // ⚠️ player 行的选择**只存在于本地 state**（选中即提交，见 `apply`），
  // 它不是服务端实体的一部分，故不进 store 也不落库（RULES §11.1）。
  const [pendingRole, setPendingRole] = useState<CreationRole>("writer");
  const selectedRole: CreationRole = (account.creation_role as CreationRole) ?? pendingRole;

  function apply(input: SetRoleInput, okTitle: string) {
    setRole.mutate(
      { userId: account.id, input },
      {
        onSuccess: (updated) =>
          pushToast({
            title: okTitle,
            detail: `${updated.display_name || updated.email} · ${
              updated.platform_role === "editor"
                ? (CREATION_ROLE_LABEL[updated.creation_role ?? ""] ?? "编辑")
                : "观众"
            }`,
            mark: "✓",
          }),
        onError: (error) =>
          pushToast({
            title: "设置失败",
            detail: error instanceof Error ? error.message : "未知错误",
            mark: "!",
          }),
      },
    );
  }

  return (
    <div className="meta-row account-user-row">
      <strong>{account.display_name || account.email}</strong>
      <span className="account-user-email">{account.email}</span>
      <StatusLabel tone={isEditor ? "success" : "warning"}>
        {isEditor ? (CREATION_ROLE_LABEL[account.creation_role ?? ""] ?? "编辑") : "观众"}
      </StatusLabel>
      <span className="account-user-actions">
        {isEditor ? (
          <>
            <Select
              id={`account-subrole-${account.id}`}
              aria-label={`${account.email} 的创作角色`}
              value={selectedRole}
              options={CREATION_ROLES}
              onChange={(event) => {
                const next = event.target.value as CreationRole;
                setPendingRole(next);
                apply({ creation_role: next }, "已更新创作角色");
              }}
            />
            <Button
              size="small"
              variant="quiet"
              disabled={setRole.isPending}
              onClick={() => apply({ platform_role: "player" }, "已降为观众")}
            >
              降为观众
            </Button>
          </>
        ) : (
          <Button
            size="small"
            variant="quiet"
            disabled={setRole.isPending}
            onClick={() =>
              apply({ platform_role: "editor", creation_role: selectedRole }, "已开通编辑身份")
            }
          >
            开通为{CREATION_ROLES.find((r) => r.value === selectedRole)?.label ?? "编辑"}
          </Button>
        )}
      </span>
    </div>
  );
}

function AllRoleChanges() {
  const logs = useRoleChangeLogs(undefined);
  return (
    <Panel
      title="全平台角色变更记录"
      description="谁 · 对谁 · 从什么 · 变成什么。追加写，不改写。"
      className="account-changes"
    >
      <RemoteState queries={[logs]} title="记录加载失败" loadingText="加载中…">
        {(logs.data ?? []).length === 0 ? (
          <EmptyState title="暂无记录" description="平台上的角色变更会出现在这里。" />
        ) : (
          <RoleChangeTable rows={logs.data ?? []} />
        )}
      </RemoteState>
    </Panel>
  );
}

function RoleChangeTable({ rows }: { rows: RoleChangeLog[] }) {
  return (
    <table className="data-table account-change-table">
      <thead>
        <tr>
          <th>时间</th>
          <th>操作</th>
          <th>对象</th>
          <th>变化</th>
          <th>备注</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{new Date(row.created_at).toLocaleString("zh-CN")}</td>
            <td>{ACTION_LABELS[row.action] ?? row.action}</td>
            {/* 「本人操作」与「他人代设」在数据上就是 actor_id ≠ user_id，如实区分。 */}
            <td>{row.actor_id === row.user_id ? "本人" : "他人代设"}</td>
            <td>
              {row.from_value} → {row.to_value}
            </td>
            <td>{row.reason || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
